import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import { 
  User, 
  Shield, 
  AlertTriangle, 
  Trash2, 
  Loader2,
  Mail,
  Building,
  Globe,
  Briefcase
} from "lucide-react";

export default function SettingsPage() {
  const { user, logout, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [showDeactivateDialog, setShowDeactivateDialog] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState("");
  const [deactivating, setDeactivating] = useState(false);

  const handleDeactivateAccount = async () => {
    if (confirmEmail.toLowerCase() !== user?.email?.toLowerCase()) {
      toast.error("Email doesn't match. Please enter your email correctly.");
      return;
    }

    setDeactivating(true);
    try {
      await axios.post(
        `${API}/auth/deactivate`,
        {},
        { headers: getAuthHeader() }
      );
      
      toast.success("Account deactivated successfully");
      logout();
      navigate("/welcome");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to deactivate account");
    } finally {
      setDeactivating(false);
      setShowDeactivateDialog(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Settings
          </h1>
          <p className="text-slate-600 mt-1">Manage your account settings and preferences</p>
        </div>

        {/* Profile Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-slate-600" />
              Profile Information
            </CardTitle>
            <CardDescription>Your account details</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-600">Full Name</Label>
                <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                  <User className="w-4 h-4 text-slate-400" />
                  <span className="text-slate-900">{user?.name || "Not set"}</span>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-600">Email</Label>
                <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                  <Mail className="w-4 h-4 text-slate-400" />
                  <span className="text-slate-900">{user?.email || "Not set"}</span>
                </div>
              </div>
              {user?.company_name && (
                <div className="space-y-2">
                  <Label className="text-slate-600">Company</Label>
                  <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                    <Building className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-900">{user.company_name}</span>
                  </div>
                </div>
              )}
              {user?.country && (
                <div className="space-y-2">
                  <Label className="text-slate-600">Country</Label>
                  <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                    <Globe className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-900">{user.country}</span>
                  </div>
                </div>
              )}
              {user?.user_type && (
                <div className="space-y-2">
                  <Label className="text-slate-600">Role</Label>
                  <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                    <Briefcase className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-900 capitalize">{user.user_type.replace(/_/g, ' ')}</span>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Security */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-slate-600" />
              Security
            </CardTitle>
            <CardDescription>Manage your account security</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="p-4 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-600">
                Your account is secured with password authentication. 
                Contact support if you need to reset your password.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Danger Zone */}
        <Card className="border-red-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Danger Zone
            </CardTitle>
            <CardDescription>Irreversible actions for your account</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="p-4 bg-red-50 rounded-lg border border-red-200">
              <h4 className="font-semibold text-red-800 mb-2">Deactivate Account</h4>
              <p className="text-sm text-red-700 mb-4">
                Once you deactivate your account, all your data including properties, budgets, 
                and reports will be permanently deleted. You will need to register again to use the platform.
              </p>
              <Button 
                variant="destructive" 
                onClick={() => setShowDeactivateDialog(true)}
                className="bg-red-600 hover:bg-red-700"
                data-testid="deactivate-account-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Deactivate My Account
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Deactivation Confirmation Dialog */}
      <Dialog open={showDeactivateDialog} onOpenChange={setShowDeactivateDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Deactivate Account
            </DialogTitle>
            <DialogDescription>
              This action cannot be undone. All your data will be permanently deleted.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800">
                <strong>Warning:</strong> Deactivating your account will permanently delete:
              </p>
              <ul className="text-sm text-red-700 mt-2 space-y-1 list-disc list-inside">
                <li>All your properties</li>
                <li>All your budgets and configurations</li>
                <li>All your reports and downloads</li>
                <li>Your subscription (if any)</li>
              </ul>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirm-email">
                Type your email to confirm: <strong>{user?.email}</strong>
              </Label>
              <Input
                id="confirm-email"
                type="email"
                placeholder="Enter your email"
                value={confirmEmail}
                onChange={(e) => setConfirmEmail(e.target.value)}
                data-testid="confirm-email-input"
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowDeactivateDialog(false);
                setConfirmEmail("");
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeactivateAccount}
              disabled={deactivating || confirmEmail.toLowerCase() !== user?.email?.toLowerCase()}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-deactivate-btn"
            >
              {deactivating ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              Yes, Deactivate My Account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
